import { useState, useEffect } from 'preact/hooks'
import { Copy, Check, AlertTriangle } from 'lucide-preact'

interface OneTimeApiKeyModalProps {
  isOpen: boolean
  apiKey: string | null
  title?: string
  // Called when the user explicitly acknowledges they've saved the key.
  // The caller is expected to clear the key from its own state immediately.
  onAcknowledge: () => void
}

/**
 * One-time reveal modal for newly generated or rotated integration API keys.
 *
 * Shows the plaintext key exactly once. The user must tick the acknowledgment
 * checkbox before dismissing the modal. The key is never persisted anywhere
 * outside the component tree and is cleared from parent state when this modal
 * unmounts.
 */
export function OneTimeApiKeyModal({ isOpen, apiKey, title = 'Save your API key', onAcknowledge }: OneTimeApiKeyModalProps) {
  const [copied, setCopied] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setCopied(false)
      setAcknowledged(false)
    }
  }, [isOpen])

  if (!isOpen || !apiKey) return null

  const handleCopy = async () => {
    if (!navigator.clipboard) return
    try {
      await navigator.clipboard.writeText(apiKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.warn('Failed to copy API key to clipboard:', err)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700">
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-300" />
            </div>
            <div>
              <h4 className="text-slate-900 dark:text-white font-semibold text-lg leading-tight">{title}</h4>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                This is the only time you&apos;ll see this key. Copy it and store it somewhere safe (password manager, secret vault, CI config) before closing this dialog.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">API Key</label>
            <div className="flex gap-2 items-stretch">
              <input
                type="text"
                value={apiKey}
                readOnly
                className="flex-1 px-3 py-2 font-mono text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 select-all"
                onFocus={e => (e.currentTarget as HTMLInputElement).select()}
              />
              <button
                type="button"
                onClick={handleCopy}
                className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-1.5 text-sm"
                aria-label="Copy API key"
              >
                {copied ? <><Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
              </button>
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => setAcknowledged((e.currentTarget as HTMLInputElement).checked)}
              className="mt-0.5 rounded border-slate-300 dark:border-slate-600"
            />
            <span>I&apos;ve copied the key and stored it somewhere safe. I understand it won&apos;t be shown again.</span>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button
            type="button"
            onClick={onAcknowledge}
            disabled={!acknowledged}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
